/**
 * TerrainWorkerPool - dispatches local terrain generation to a pool of workers.
 *
 * Mirrors the mesh-worker pattern: requests are keyed by id and resolved via a
 * callback map so generation runs in parallel off the main thread. Async
 * responses feed VoxelWorld's existing receive* methods, exactly like the
 * network path.
 *
 * The pool is scaled to the machine (terrainWorkerCount) — generation is the
 * streaming bottleneck, so give it most cores while leaving headroom for the
 * main thread + the separate mesh workers.
 *
 * Dispatch is by COLUMN AFFINITY: every request touching a 2D column (cx,cz) — its tile/column
 * bundle and each of its vertical chunks — prefers the same worker. Each worker keeps its own
 * unshared `rawChunk` cache (LocalTerrainSource), so a column bundle's generation populates that
 * cache and the follow-up per-chunk requests hit it instead of regenerating the whole
 * stamp-inclusive column elsewhere.
 *
 * WHAT ORDERING THE POOL OWES ITS CALLERS, and did not used to keep. Affinity was once a raw hash
 * posted straight to a worker, which made completion order a permutation of request order chosen by
 * that hash — 16% of pairs inverted at six workers, one column finishing 39 places out, one worker
 * holding 19 jobs while another held 4. Every ordering decision upstream was being shuffled after
 * the fact. `WorkerDispatch` now holds a central queue in submit order and hands jobs out as slots
 * free, keeping affinity as a preference rather than a rule; see that module for the policy and the
 * measurements.
 */

import type { VoxelChunkData, MapTileResponse, SurfaceColumnResponse, WorldGenConfig } from '@worldify/shared';
import { terrainWorkerBudget } from './workerBudget.js';
import { perfStats } from '../debug/PerformanceStats.js';
import type { TerrainWorkerResponse } from './terrainWorker.js';
import type { VoxelLodChunkData, LodColumnResponse, LodColumnBundleResponse } from './LocalTerrainSource.js';
import { WorkerDispatch } from './workerDispatch.js';

/** Column key for affinity — the same pair packing VoxelWorld uses, inlined to avoid the import. */
function columnKey(a: number, b: number): number {
  return (a + 0x10000) * 0x20000 + (b + 0x10000);
}

/** A queued job: the message to post, and who to call with the answer. */
interface TerrainJob {
  msg: Record<string, unknown>;
  cb: (data: unknown) => void;
}

/**
 * Number of terrain-generation workers, scaled to the machine. Generation is the streaming bottleneck,
 * so give it most cores while leaving headroom for the main thread + the (separate) mesh workers. Also
 * used to size VoxelWorld's in-flight request window so the two stay in sync (a worker is only useful if
 * the request layer keeps it fed). Capped so a many-core box doesn't spawn a worker per core — each owns
 * a TerrainGenerator + cave caches (memory), with diminishing returns past ~6.
 */
export function terrainWorkerCount(): number {
  return terrainWorkerBudget(); // shares one thread budget with the mesh pool — see workerBudget.ts
}

export class TerrainWorkerPool {
  private readonly workers: Worker[] = [];
  /** id → { lane, cb }. The lane rides along so a response can free the slot it occupied. */
  private readonly callbacks = new Map<number, { lane: number; cb: (data: unknown) => void }>();
  private nextId = 0;
  private readonly dispatch: WorkerDispatch<TerrainJob>;

  constructor(seed: number, genConfig?: WorldGenConfig, poolSize?: number) {
    const size = poolSize ?? terrainWorkerCount();
    for (let i = 0; i < size; i++) {
      const worker = new Worker(new URL('./terrainWorker.ts', import.meta.url), { type: 'module' });
      worker.postMessage({ type: 'init', seed, genConfig });
      worker.onmessage = (e: MessageEvent<TerrainWorkerResponse>) => {
        const entry = this.callbacks.get(e.data.id);
        if (!entry) return;
        this.callbacks.delete(e.data.id);
        // OFF-FRAME main-thread work: the reply is handled between rAF callbacks, where no
        // frame-scoped timer sees it. Counted so the panel can show it (see PerformanceStats).
        perfStats.begin('offFrame');
        perfStats.begin('offGen');
        // Free the slot BEFORE the callback: handlers re-enter the pool (a tile's arrival requests
        // its chunks), and a slot still marked busy would send that straight to the back of a queue
        // it could have skipped.
        this.dispatch.complete(entry.lane);
        entry.cb(e.data.data);
        perfStats.end('offGen');
        perfStats.end('offFrame');
      };
      this.workers.push(worker);
    }
    this.dispatch = new WorkerDispatch<TerrainJob>(size, (lane, job) => {
      const id = this.nextId++;
      this.callbacks.set(id, { lane, cb: job.cb });
      this.workers[lane].postMessage({ ...job.msg, id });
    });
  }

  private submit(a: number, b: number, msg: Record<string, unknown>, cb: (data: unknown) => void): void {
    this.dispatch.submit(columnKey(a, b), { msg, cb });
  }

  requestChunk(cx: number, cy: number, cz: number, cb: (data: VoxelChunkData) => void): void {
    this.submit(cx, cz, { type: 'chunk', cx, cy, cz }, cb as (data: unknown) => void);
  }

  requestTile(tx: number, tz: number, cb: (data: MapTileResponse) => void): void {
    this.submit(tx, tz, { type: 'tile', tx, tz }, cb as (data: unknown) => void);
  }

  requestColumn(tx: number, tz: number, cb: (data: SurfaceColumnResponse) => void): void {
    this.submit(tx, tz, { type: 'column', tx, tz }, cb as (data: unknown) => void);
  }

  /** Coarse (LOD ≥ 1) chunk. Column affinity on the coarse (cx,cz) keeps a coarse column's
   *  height-cache warm on one worker, mirroring the LOD-0 tile→chunks pattern. */
  requestLodChunk(cx: number, cy: number, cz: number, lod: number, cb: (data: VoxelLodChunkData) => void): void {
    this.submit(cx, cz, { type: 'lodChunk', cx, cy, cz, lod }, cb as (data: unknown) => void);
  }

  /** Vertical chunk range for a coarse column (LOD ring sky/underground clip). */
  requestLodColumn(cx: number, cz: number, lod: number, cb: (data: LodColumnResponse) => void): void {
    this.submit(cx, cz, { type: 'lodColumn', cx, cz, lod }, cb as (data: unknown) => void);
  }

  /**
   * A coarse column's clip AND its chunks in ONE job — the coarse counterpart of `requestColumn`.
   * Column affinity is the same as the per-chunk path, so the bundle warms the lane its follow-ups
   * will land on. `minCy`/`maxCy` are the caller's wanted window; the column's own clip narrows it.
   */
  requestLodColumnBundle(
    cx: number, cz: number, lod: number, minCy: number, maxCy: number, maxChunks: number,
    cb: (data: LodColumnBundleResponse) => void,
  ): void {
    this.submit(cx, cz, { type: 'lodColumnBundle', cx, cz, lod, minCy, maxCy, maxChunks },
      cb as (data: unknown) => void);
  }

  /** A spawn point on the path network, or null in a world without paths. One-off at world load;
   *  the answer may lazily trace the origin neighbourhood's paths, which is the point of asking a
   *  worker. Keyed to the origin column so it shares that lane's warm caches. */
  requestPathSpawn(cb: (data: { x: number; y: number; z: number } | null) => void): void {
    this.submit(0, 0, { type: 'pathSpawn' }, cb as (data: unknown) => void);
  }

  /** Queue depth and per-worker load — surfaced for the [lod-audit] streaming line. */
  stats(): { queued: number; inFlight: readonly number[] } {
    return { queued: this.dispatch.queued, inFlight: this.dispatch.inFlight };
  }

  dispose(): void {
    for (const worker of this.workers) worker.terminate();
    this.workers.length = 0;
    this.callbacks.clear();
    this.dispatch.clear();
  }
}
